import {
  BackgroundActivity,
  BackgroundActivityKind,
  BackgroundActivityStatus,
  BackgroundActivityTargetKind,
  BackgroundJobActivityTarget,
  UploadActivity,
  serializeActivityTarget
} from '@models/backgroundActivities';
import { randomUUID } from '@random';

import { useBackgroundActivityStore } from './backgroundActivityStore';

type PayloadForKind<K extends BackgroundActivityKind> = K extends BackgroundActivityKind.UPLOAD
  ? UploadActivity['payload']
  : never;

type ResultForKind<K extends BackgroundActivityKind> = K extends BackgroundActivityKind.UPLOAD
  ? NonNullable<UploadActivity['result']>
  : never;

interface RegisterParams<K extends BackgroundActivityKind> {
  kind: K;
  target: BackgroundJobActivityTarget;
  payload: PayloadForKind<K>;
  onCancel: () => void;
}

export class BackgroundActivityRegistry {
  private cancelHandlers: Map<string, () => void> = new Map();

  private get store() {
    return useBackgroundActivityStore.getState();
  }

  register<K extends BackgroundActivityKind>(params: RegisterParams<K>): string {
    const targetKey = serializeActivityTarget(params.target);

    const existingId = this.store.byTargetKey[targetKey];
    if (existingId) {
      const existing = this.store.activities[existingId];
      if (
        existing &&
        (existing.status === BackgroundActivityStatus.PENDING ||
          existing.status === BackgroundActivityStatus.RUNNING)
      ) {
        throw new Error(`BackgroundActivityRegistry: activity already running for ${targetKey}`);
      }
      this.store.removeActivity(existingId);
      this.cancelHandlers.delete(existingId);
    }

    const id = randomUUID();

    const activity = {
      id,
      kind: params.kind,
      targetKey,
      target: params.target,
      status: BackgroundActivityStatus.PENDING,
      progress: null,
      startedAt: Date.now(),
      payload: params.payload,
      result: null,
      error: null
    } as BackgroundActivity;

    this.store.upsertActivity(activity);
    this.cancelHandlers.set(id, params.onCancel);

    return id;
  }

  markRunning(id: string): void {
    if (!this.store.activities[id]) return;
    this.store.patchActivity(id, { status: BackgroundActivityStatus.RUNNING });
  }

  updateProgress(id: string, progress: number): void {
    if (!this.store.activities[id]) return;
    this.store.patchActivity(id, { progress });
  }

  complete<K extends BackgroundActivityKind>(id: string, kind: K, result: ResultForKind<K>): void {
    const activity = this.store.activities[id];
    if (!activity || activity.kind !== kind) return;
    this.store.patchActivity(id, {
      status: BackgroundActivityStatus.COMPLETED,
      result
    } as Partial<BackgroundActivity>);
    this.cancelHandlers.delete(id);
  }

  fail(id: string, error: string): void {
    if (!this.store.activities[id]) return;
    this.store.patchActivity(id, {
      status: BackgroundActivityStatus.FAILED,
      error
    });
    this.cancelHandlers.delete(id);
  }

  cancel(id: string): void {
    const onCancel = this.cancelHandlers.get(id);
    if (onCancel) onCancel();
    this.cancelHandlers.delete(id);
    this.store.removeActivity(id);
  }

  cancelAll(): void {
    const ids = Object.keys(this.store.activities);
    for (const id of ids) {
      try {
        this.cancel(id);
      } catch (error) {
        console.error(`BackgroundActivityRegistry.cancelAll failed for ${id}`, error);
      }
    }
  }

  markConsumed(activityId: string): void {
    const activity = this.store.activities[activityId];
    if (!activity) return;
    if (
      activity.status !== BackgroundActivityStatus.COMPLETED &&
      activity.status !== BackgroundActivityStatus.FAILED
    ) {
      return;
    }
    this.store.removeActivity(activityId);
    this.cancelHandlers.delete(activityId);
  }

  getByActivityId(id: string): BackgroundActivity | null {
    return this.store.activities[id] ?? null;
  }

  getByTargetKey(key: string): BackgroundActivity | null {
    const id = this.store.byTargetKey[key];
    return id ? (this.store.activities[id] ?? null) : null;
  }

  getByTaskEntityId(taskEntityId: string): BackgroundActivity[] {
    return Object.values(this.store.activities).filter((a) => {
      const t = a.target;
      return (
        (t.kind === BackgroundActivityTargetKind.TASK_BLOCK_CONTENT ||
          t.kind === BackgroundActivityTargetKind.TASK_BLOCK_FILE_SLOT ||
          t.kind === BackgroundActivityTargetKind.TASK_BLOCK_STATIC_ATTACHMENTS) &&
        t.taskEntityId === taskEntityId
      );
    });
  }

  list(): BackgroundActivity[] {
    return Object.values(this.store.activities);
  }

  hasInFlight(): boolean {
    for (const a of Object.values(this.store.activities)) {
      if (
        a.status === BackgroundActivityStatus.PENDING ||
        a.status === BackgroundActivityStatus.RUNNING
      ) {
        return true;
      }
    }
    return false;
  }
}
